/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : GrantAccessDrawer.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import CustomButtonV2 from "../../../../components/customComponentsV2/common/button/CustomButtonV2";
import userManagementApis from "../../../../redux/actions/userManagent";
import {
  HandleApiActions,
  showToastMessage,
} from "../../../../redux/actions/actionHandler";
import ConflictViewer from "./ConflictViewer";
import CustomSelectV2 from "../../../../components/customComponentsV2/common/select/CustomSelectV2";
import deleteIcon from "../../../../assets/delete_icon.png";

const PERMISSION_LEVELS = [
  { value: "OWNER", label: "Owner" },
  { value: "EDITOR", label: "Editor" },
  { value: "VIEWER", label: "Viewer" },
];

function normalizeConflicts(apiResult, usersById) {
  const conflicts = [];

  for (const e of apiResult?.errors || []) {
    conflicts.push({
      type: "ERROR",
      user_id: e.user_id,
      user_label: usersById[e.user_id] || e.user_id,
      reason: e.error,
      details: "This access cannot be granted",
      blocking: true,
    });
  }

  if (!apiResult?.warnings?.length) return conflicts;

  for (const w of apiResult.warnings) {
    const userLabel = usersById[w.user_id] || w.user_id;
    let added = false;

    for (const c of w.sameLevelAccess || []) {
      added = true;
      conflicts.push({
        type: "DUPLICATE",
        user_id: w.user_id,
        user_label: userLabel,
        reason: "Access already exists at same level",
        details: `Existing permission: ${c.existing_permission} (${c.role})`,
        blocking: false,
      });
    }

    for (const c of w.inheritedAccess || []) {
      added = true;
      conflicts.push({
        type: "INHERITED",
        user_id: w.user_id,
        user_label: userLabel,
        reason: `Inherited from ${c.inherited_from_scope}`,
        details: `Role: ${c.role}`,
        blocking: false,
      });
    }

    for (const c of w.policyConflicts || []) {
      added = true;
      conflicts.push({
        type: "POLICY",
        user_id: w.user_id,
        user_label: userLabel,
        reason: "Policy conflict",
        details: `Action denied: ${c.action}`,
        blocking: true,
      });
    }

    if (!added) {
      conflicts.push({
        type: "CONFIRMATION",
        user_id: w.user_id,
        user_label: userLabel,
        reason: "Confirmation required",
        details: "User already has effective access",
        blocking: false,
      });
    }
  }

  return conflicts;
}

function resolveResourceIds(context) {
  switch (context.access_type) {
    case "PROJECT":
      return [context.project_id];
    case "VARIANT":
      return [context.variant_id];
    case "EE":
      return [context.ee_id];
    case "SWM":
      return [context.swm_id];
    case "VS":
      return [context.vs_id];
    case "VC":
      return [context.vc_id];
    default:
      throw new Error("INVALID_SCOPE_CONTEXT");
  }
}

function GrantAccessDrawer({
  open,
  onClose,
  context,
  supplierNamesList,
  entityAllUsersList,
}) {
  const dispatch = useDispatch();

  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [conflictView, setConflictView] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState();

  const resetAndClose = () => {
    setRows([]);
    setResult(null);
    setConflictView([]);
    setSubmitting(false);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setRows([]);
      setResult(null);
      setConflictView([]);
    }
  }, [open]);

  useEffect(() => {
    if (!users) return;

    const filteredSuppliers = users.filter(
      (data) => data?.role_details?.description === supplierNamesList?.[0],
    );

    const filteredNotSuppliersTAReviewer = users.filter(
      (data) =>
        data?.role_details?.capability_code?.toUpperCase() !==
        "EXTERNAL_SUPPLIER_USER",
    );

    const newAllUserArray = [
      ...filteredSuppliers,
      ...filteredNotSuppliersTAReviewer,
    ];

    setFilteredUsers(newAllUserArray);
  }, [users, supplierNamesList]);

  useEffect(() => {
    let newUsers = filteredUsers.filter(
      (au) => !(entityAllUsersList || []).some((eu) => eu.user_id === au.user_id),
    );
    setAvailableUsers(newUsers);
  }, [rows, filteredUsers, entityAllUsersList]);

  useEffect(() => {
    if (!open) return;

    dispatch(
      HandleApiActions({
        ...userManagementApis.get_users_list_pa,
        show_toast: false,
        params: { page_number: 1, page_size: 500 },
      }),
    ).then((resp) => {
      if (!resp?.error) setUsers(resp.data || []);
    });
  }, [open, dispatch]);

  useEffect(() => {
    if (!result) return;

    if (result.status === "WARNING" || result.status === "ERROR") {
      const usersById = Object.fromEntries(
        users.map((u) => [u.user_id, `${u.fullname} (${u.email})`]),
      );
      setConflictView(normalizeConflicts(result, usersById));
    } else {
      setConflictView([]);
    }
  }, [result, users]);

  const rowCounterRef = useRef(0);

  const addRow = () => {
    rowCounterRef.current += 1;
    setRows((r) => [
      ...r,
      { id: `row-${Date.now()}-${rowCounterRef.current}`, user_id: "", permission_level: "VIEWER" },
    ]);
  };

  const removeRow = (index) => {
    setRows((prevRows) => prevRows.filter((_, i) => i !== index));
  };

  const updateRow = (idx, key, value) =>
    setRows((r) =>
      r.map((row, i) => (i === idx ? { ...row, [key]: value } : row)),
    );

  const submit = async (confirm = false) => {
    setSubmitting(true);

    let resource_ids;
    try {
      resource_ids = resolveResourceIds(context);
    } catch {
      setSubmitting(false);
      return;
    }

    if (!resource_ids[0]) {
      showToastMessage({
        type: "error",
        message: "No valid resource selected",
      });
      setSubmitting(false);
      return;
    }

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...userManagementApis.grant_project_access,
          show_toast: false,
          params: {
            scope: context.access_type,
            resource_ids,
            grants: rows.map(({ user_id, permission_level }) => ({
              user_id,
              permission_level,
            })),
            confirm,
          },
        }),
      ),
    );

    setSubmitting(false);
    setResult(resp || null);
  };

  const userDetailsAll = (user_id) => {
    let seletedUsers = users?.find((data) => data?.user_id === user_id);
    return seletedUsers?.fullname;
  };

  return (
    <Drawer open={open} anchor="right" PaperProps={{ sx: { width: 520 } }}>
      <Box
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography fontWeight={600}>Manage Access</Typography>
          <IconButton onClick={resetAndClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ flexGrow: 1 }}>
          {rows.map((row, idx) => (
            <Box key={row.id || row.user_id || `row-${idx}`} sx={{ display: "flex", gap: 1, mb: 1 }}>
              <CustomSelectV2
                showLabel
                heightExpand={true}
                enableSearch={true}
                required
                label="User"
                optionsList={
                  availableUsers?.map((u) => ({
                    label: `${u.fullname} (${u.email})`,
                    value: u.user_id,
                  })) || []
                }
                selectedValue={row.user_id}
                handleOnChange={(e) =>
                  updateRow(idx, "user_id", e.target.value)
                }
              />

              <CustomSelectV2
                showLabel
                heightExpand={true}
                enableSearch={true}
                required
                label="Permission Level"
                optionsList={PERMISSION_LEVELS || []}
                selectedValue={row.permission_level}
                handleOnChange={(e) =>
                  updateRow(idx, "permission_level", e.target.value)
                }
              />

              <IconButton
                size="small"
                sx={{ p: 0, mt: 2 }}
                onClick={() => removeRow(idx)}
              >
                <img
                  src={deleteIcon}
                  alt="delete"
                  style={{ width: 24, height: 24 }}
                />
              </IconButton>
            </Box>
          ))}

          <CustomButtonV2
            btnName="Add User"
            size="small"
            handleOnClick={addRow}
          />
        </Box>

        {result?.status === "WARNING" && conflictView.length > 0 && (
          <ConflictViewer
            conflicts={conflictView}
            onConfirm={() => submit(true)}
            onCancel={() => {
              setConflictView([]);
              setResult(null);
            }}
          />
        )}

        {result?.status === "SUCCESS" && result.success?.length > 0 && (
          <Typography color="success.main">
            Access granted successfully
          </Typography>
        )}

        {result?.status === "ERROR" && (
          <Box sx={{ mt: 2 }}>
            <Typography color="error.main" fontWeight={600}>
              Access could not be granted
            </Typography>

            {result.errors.map((e) => (
              <Typography key={`${e.user_id || "err"}-${e.error}`} fontSize={13}>
                {userDetailsAll(e.user_id)}: {e.error}
              </Typography>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <CustomButtonV2 btnName="Cancel" handleOnClick={resetAndClose} />
          <CustomButtonV2
            btnName="Grant Access"
            disabled={!rows.length || submitting}
            handleOnClick={() => submit(false)}
          />
        </Box>
      </Box>
    </Drawer>
  );
}

GrantAccessDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  context: PropTypes.shape({
    access_type: PropTypes.string,
    project_id: PropTypes.string,
    variant_id: PropTypes.string,
    ee_id: PropTypes.string,
    swm_id: PropTypes.string,
    vs_id: PropTypes.string,
    vc_id: PropTypes.string,
  }),
  supplierNamesList: PropTypes.arrayOf(PropTypes.string),
  entityAllUsersList: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
};

export default GrantAccessDrawer;
